{% block sellops_order_modification_tracking_list %}
<sw-page class="sellops-order-modification-tracking-list">

    {% block sellops_order_modification_tracking_list_smart_bar_header %}
    <template #smart-bar-header>
        <h2>{{ $tc('sellops-order-modification-tracking.list.title') }}</h2>
    </template>
    {% endblock %}

    {% block sellops_order_modification_tracking_list_smart_bar_actions %}
    <template #smart-bar-actions>
        <sw-button @click="toggleColumnManager" size="small" variant="ghost">
            {{ $tc('sellops-order-modification-tracking.list.reorderColumnsButton') }}
        </sw-button>
        <sw-button @click="goToSettings" size="small" variant="ghost">
            <sw-icon name="regular-cog" small></sw-icon>
            {{ $tc('sellops-order-modification-tracking.general.settingsLink') }}
        </sw-button>
    </template>
    {% endblock %}

    {% block sellops_order_modification_tracking_list_content %}
    <template #content>
        <div class="sellops-tracking-wrapper">

            {% block sellops_tracking_column_manager %}
            <div v-if="isColumnManagerOpen" class="sellops-tracking-column-manager">
                <div class="sellops-tracking-column-manager__header">
                    <span>{{ $tc('sellops-order-modification-tracking.list.columnManagerTitle') }}</span>
                    <sw-button size="x-small" variant="ghost" @click="resetColumnOrder">
                        {{ $tc('sellops-order-modification-tracking.list.resetColumnOrder') }}
                    </sw-button>
                </div>
                <div class="sellops-tracking-column-manager__pills">
                    <div
                        v-for="(col, index) in orderedColumns"
                        :key="col.property"
                        class="sellops-tracking-column-manager__pill"
                        :class="{ 'is--drag-over': dragOverColumnIndex === index }"
                        draggable="true"
                        @dragstart="onColumnDragStart(index)"
                        @dragover="onColumnDragOver($event, index)"
                        @drop="onColumnDrop(index)"
                        @dragend="onColumnDragEnd"
                    >
                        <sw-icon name="regular-bars" x-small></sw-icon>
                        {{ col.label }}
                    </div>
                </div>
            </div>
            {% endblock %}

            {% block sellops_tracking_summary %}
            <div class="sellops-tracking-summary">
                <div class="sellops-tracking-summary__card sellops-tracking-summary__card--warning">
                    <div class="sellops-tracking-summary__count">{{ pendingCount }}</div>
                    <div class="sellops-tracking-summary__label">{{ $tc('sellops-order-modification-tracking.list.summaryPendingPayments') }}</div>
                </div>
                <div class="sellops-tracking-summary__card sellops-tracking-summary__card--danger">
                    <div class="sellops-tracking-summary__count">{{ refundCount }}</div>
                    <div class="sellops-tracking-summary__label">{{ $tc('sellops-order-modification-tracking.list.summaryRefundsRequired') }}</div>
                </div>
                <div class="sellops-tracking-summary__card sellops-tracking-summary__card--success">
                    <div class="sellops-tracking-summary__count">{{ completedCount }}</div>
                    <div class="sellops-tracking-summary__label">{{ $tc('sellops-order-modification-tracking.list.summaryCompleted') }}</div>
                </div>
            </div>
            {% endblock %}

            {% block sellops_tracking_filters %}
            <div class="sellops-tracking-filters">
                <sw-single-select
                    :options="paymentStatusOptions"
                    v-model="filterPaymentStatus"
                    :label="$tc('sellops-order-modification-tracking.list.filterPaymentStatus')"
                    size="small"
                    @change="onFilterChange"
                ></sw-single-select>
                <sw-single-select
                    :options="changeTypeOptions"
                    v-model="filterChangeType"
                    :label="$tc('sellops-order-modification-tracking.list.filterChangeType')"
                    size="small"
                    @change="onFilterChange"
                ></sw-single-select>
                <sw-single-select
                    :options="modificationTypeOptions"
                    v-model="filterModificationType"
                    :label="$tc('sellops-order-modification-tracking.list.filterModificationType')"
                    size="small"
                    @change="onFilterChange"
                ></sw-single-select>
            </div>
            {% endblock %}

            {% block sellops_tracking_grid_header %}
            <div class="sellops-tracking-grid-header">
                <h3 class="sellops-tracking-grid-header__title">
                    {{ $tc('sellops-order-modification-tracking.list.gridTitle') }}
                </h3>
                <div v-if="total > 0" class="sellops-tracking-pagination">
                    <button class="sellops-tracking-pagination__btn" :disabled="page <= 1" @click="onPageNav(page - 1)">&#8249;</button>
                    <template v-if="visiblePages[0] > 1">
                        <button class="sellops-tracking-pagination__btn" @click="onPageNav(1)">1</button>
                        <span v-if="visiblePages[0] > 2" class="sellops-tracking-pagination__ellipsis">…</span>
                    </template>
                    <button
                        v-for="p in visiblePages"
                        :key="p"
                        class="sellops-tracking-pagination__btn"
                        :class="{ 'is--active': p === page }"
                        @click="onPageNav(p)"
                    >{{ p }}</button>
                    <template v-if="visiblePages[visiblePages.length - 1] < totalPages">
                        <span v-if="visiblePages[visiblePages.length - 1] < totalPages - 1" class="sellops-tracking-pagination__ellipsis">…</span>
                        <button class="sellops-tracking-pagination__btn" @click="onPageNav(totalPages)">{{ totalPages }}</button>
                    </template>
                    <button class="sellops-tracking-pagination__btn" :disabled="page >= totalPages" @click="onPageNav(page + 1)">&#8250;</button>
                    <span class="sellops-tracking-pagination__per-page">
                        Items per page:
                        <select :value="limit" @change="onLimitChange">
                            <option value="10">10</option>
                            <option value="25">25</option>
                            <option value="50">50</option>
                        </select>
                    </span>
                </div>
            </div>
            {% endblock %}

            {% block sellops_tracking_grid %}
            <sw-data-grid
                :data-source="items"
                :columns="orderedColumns"
                :is-loading="isLoading"
                :show-selection="false"
                :show-settings="false"
                :allow-column-edit="false"
                :sort-by="sortBy"
                :sort-direction="sortDirection"
                @column-sort="onSortColumn"
            >

                {% block sellops_tracking_grid_column_modification_type %}
                <template #column-modificationType="{ item }">
                    <sw-label :variant="item.modificationType === 'replacement' ? 'warning' : (item.modificationType === 'supplement' ? 'info' : 'neutral')" size="small" appearance="pill">
                        {{ getModificationTypeLabel(item.modificationType) }}
                    </sw-label>
                </template>
                {% endblock %}

                {% block sellops_tracking_grid_column_change_type %}
                <template #column-changeType="{ item }">
                    <sw-label :variant="getVariantForChangeType(item.changeType)" size="small">
                        {{ $tc('sellops-order-modification-tracking.list.changeType' + capitalize(item.changeType)) }}
                    </sw-label>
                </template>
                {% endblock %}

                {% block sellops_tracking_grid_column_parent_order_number %}
                <template #column-parentOrderNumber="{ item }">
                    <a class="sellops-tracking-order-link" href="#" @click.prevent="onOpenParentOrder(item)">
                        {{ item.parentOrderNumber }}
                    </a>
                </template>
                {% endblock %}

                {% block sellops_tracking_grid_column_child_order_number %}
                <template #column-childOrderNumber="{ item }">
                    <a class="sellops-tracking-order-link" href="#" @click.prevent="onOpenChildOrder(item)">
                        {{ item.childOrderNumber }}
                    </a>
                </template>
                {% endblock %}

                {% block sellops_tracking_grid_column_difference_amount %}
                <template #column-differenceAmount="{ item }">
                    <span :class="item.differenceAmount < 0 ? 'sellops-tracking-amount--negative' : (item.differenceAmount > 0 ? 'sellops-tracking-amount--positive' : '')">
                        {{ item.differenceAmount > 0 ? '+' : '' }}{{ formatCurrency(item.differenceAmount, item.currencyIso) }}
                    </span>
                </template>
                {% endblock %}

                {% block sellops_tracking_grid_column_parent_total %}
                <template #column-parentOrderTotal="{ item }">
                    {{ formatCurrency(item.parentOrderTotal, item.currencyIso) }}
                </template>
                {% endblock %}

                {% block sellops_tracking_grid_column_child_total %}
                <template #column-childOrderTotal="{ item }">
                    {{ formatCurrency(item.childOrderTotal, item.currencyIso) }}
                </template>
                {% endblock %}

                {% block sellops_tracking_grid_column_payment_status %}
                <template #column-paymentStatus="{ item }">
                    <sw-single-select
                        :value="item.paymentStatus"
                        :options="paymentStatusSelectOptions"
                        size="small"
                        @change="(val) => onPaymentStatusChange(item, val)"
                    ></sw-single-select>
                </template>
                {% endblock %}

                {% block sellops_tracking_grid_column_refund_amount %}
                <template #column-refundAmount="{ item }">
                    <span v-if="item.refundStatus && item.refundStatus !== 'not_applicable'" class="sellops-tracking-amount--negative">
                        {{ formatCurrency(item.refundAmount, item.currencyIso) }}
                    </span>
                    <span v-else class="sellops-tracking-na">
                        {{ $tc('sellops-order-modification-tracking.list.refundNotApplicable') }}
                    </span>
                </template>
                {% endblock %}

                {% block sellops_tracking_grid_column_refund_status %}
                <template #column-refundStatus="{ item }">
                    <sw-single-select
                        v-if="item.refundStatus && item.refundStatus !== 'not_applicable'"
                        :value="item.refundStatus"
                        :options="refundStatusSelectOptions"
                        size="small"
                        @change="(val) => onRefundStatusChange(item, val)"
                    ></sw-single-select>
                    <span v-else class="sellops-tracking-na">
                        {{ $tc('sellops-order-modification-tracking.list.refundNotApplicable') }}
                    </span>
                </template>
                {% endblock %}

                {% block sellops_tracking_grid_column_created_at %}
                <template #column-createdAt="{ item }">
                    {{ item.createdAt ? new Date(item.createdAt).toLocaleString() : '–' }}
                </template>
                {% endblock %}

                {% block sellops_tracking_grid_actions %}
                <template #actions="{ item }">
                    <sw-context-menu-item @click="onOpenParentOrder(item)">
                        {{ $tc('sellops-order-modification-tracking.list.contextViewParent') }}
                    </sw-context-menu-item>
                    <sw-context-menu-item @click="onOpenChildOrder(item)">
                        {{ $tc('sellops-order-modification-tracking.list.contextViewChild') }}
                    </sw-context-menu-item>
                </template>
                {% endblock %}

                {% block sellops_tracking_grid_empty_state %}
                <template #empty-state>
                    <sw-empty-state
                        :title="$tc('sellops-order-modification-tracking.list.emptyStateTitle')"
                        :subline="$tc('sellops-order-modification-tracking.list.emptyStateMessage')"
                    ></sw-empty-state>
                </template>
                {% endblock %}

            </sw-data-grid>
            {% endblock %}


        </div>
    </template>
    {% endblock %}

</sw-page>
{% endblock %}
